import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import logoMirai from "../assets/mirai_innovation_logo.jpeg";
import "../styles/DemographicResults.css";
import Navbar from '../components/Navbar';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { PLATFORM_PDF_FOOTER } from '../constants/platform';

// Estilos para el PDF
const pdfStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    borderBottom: '2 solid #667eea',
    paddingBottom: 15,
  },
  logo: {
    width: 60,
    height: 60,
    marginRight: 15,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#667eea',
    flex: 1,
  },
  section: {
    marginBottom: 20,
    padding: 15,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    border: '1 solid #e9ecef',
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: 10,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingVertical: 4,
  },
  label: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#6c757d',
    flex: 1,
  },
  value: {
    fontSize: 12,
    color: '#2c3e50',
    flex: 2,
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: 'center',
    fontSize: 10,
    color: '#6c757d',
    borderTop: '1 solid #e9ecef',
    paddingTop: 10,
  },
  watermark: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 120,
    color: '#f0f0f0',
    opacity: 0.1,
    zIndex: -1,
  },
});

// Componente PDF
const DemographicPDF = ({ userData, demographicData }) => {
  const currentDate = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Marca de agua */}
        <Text style={pdfStyles.watermark}>MIRAI</Text>

        {/* Header */}
        <View style={pdfStyles.header}>
          <Image src={logoMirai} style={pdfStyles.logo} />
          <Text style={pdfStyles.title}>Datos Demográficos del Estudiante</Text>
        </View>

        {/* Información Personal */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Información Personal</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Número de Boleta:</Text>
            <Text style={pdfStyles.value}>{demographicData.student_id || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Nombre Completo:</Text>
            <Text style={pdfStyles.value}>{demographicData.full_name || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Correo Institucional:</Text>
            <Text style={pdfStyles.value}>{demographicData.institutional_email || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Correo Personal:</Text>
            <Text style={pdfStyles.value}>{demographicData.personal_email || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Sexo:</Text>
            <Text style={pdfStyles.value}>
              {demographicData.sex === 'Otro' ? demographicData.other_sex : demographicData.sex || 'N/A'}
            </Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Rango de Edad:</Text>
            <Text style={pdfStyles.value}>{demographicData.age_range || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Estado:</Text>
            <Text style={pdfStyles.value}>{demographicData.state || 'N/A'}</Text>
          </View>
        </View>

        {/* Información Académica */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Información Académica</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Tipo de Universidad:</Text>
            <Text style={pdfStyles.value}>{demographicData.university_type || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Área de Conocimiento:</Text>
            <Text style={pdfStyles.value}>{demographicData.knowledge_area || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Turno:</Text>
            <Text style={pdfStyles.value}>{demographicData.shift || 'N/A'}</Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Semestre:</Text>
            <Text style={pdfStyles.value}>{demographicData.semester || 'N/A'}</Text>
          </View>
        </View>

        {/* Intereses y Aspiraciones */}
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.sectionTitle}>Intereses y Aspiraciones</Text>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Disposición para Trabajar:</Text>
            <Text style={pdfStyles.value}>
              {demographicData.wants_to_work === 'Otra' ? demographicData.other_work : demographicData.wants_to_work || 'N/A'}
            </Text>
          </View>
          <View style={pdfStyles.row}>
            <Text style={pdfStyles.label}>Interés en IA y Robótica:</Text>
            <Text style={pdfStyles.value}>
              {demographicData.wants_ai_robotics === 'Otra' ? demographicData.other_ai_robotics : demographicData.wants_ai_robotics || 'N/A'}
            </Text>
          </View>
        </View>

        {/* Footer */}
        <Text style={pdfStyles.footer}>
          {PLATFORM_PDF_FOOTER}
        </Text>
      </Page>
    </Document>
  );
};

const DemographicResults = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [demographicData, setDemographicData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const fetchDemographicData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // Obtener datos del usuario
        const userResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/me`, {
          headers: { Authorization: token },
        });
        setUserData(userResponse.data);

        // Obtener datos demográficos
        const demographicResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/demographic-data`, {
          headers: { Authorization: token },
        });
        setDemographicData(demographicResponse.data);

      } catch (error) {
        console.error("Error al obtener datos demográficos:", error);
        alert("Error al cargar los datos demográficos");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDemographicData();
  }, [navigate]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="dashboard-layout">
          <div className="dashboard-sidebar">
            <div className="sidebar-header"><h3>Datos Demográficos</h3></div>
          </div>
          <div className="dashboard-main">
            <div className="loading-container text-center mt-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="mt-2">Cargando datos demográficos...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!demographicData) {
    return (
      <>
        <Navbar />
        <div className="dashboard-layout">
          <div className="dashboard-sidebar">
            <div className="sidebar-header"><h3>Datos Demográficos</h3></div>
            <div className="sidebar-menu">
              <button className="menu-item return-dashboard" onClick={() => navigate("/dashboard")}>
                <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
              </button>
            </div>
          </div>
          <div className="dashboard-main">
            <div className="error-container text-center mt-5">
              <h2>No se encontraron datos demográficos</h2>
              <p>Parece que no has completado el cuestionario demográfico.</p>
              <button
                onClick={() => navigate("/demographic-survey")}
                className="btn btn-primary"
              >
                Completar Cuestionario
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="dashboard-layout">
        {/* Sidebar */}
        <div className="dashboard-sidebar">
          <div className="sidebar-header">
            <h3>Datos Demográficos</h3>
          </div>
          <div className="sidebar-menu">
            <button
              className={`menu-item ${activeSection === "overview" ? "active" : ""}`}
              onClick={() => setActiveSection("overview")}
            >
              <i className="bi bi-speedometer2"></i> Resumen
            </button>
            <button
              className="menu-item return-dashboard"
              onClick={() => navigate("/dashboard")}
            >
              <i className="bi bi-arrow-left-circle"></i> Volver al Dashboard
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="dashboard-main">
          <div className="demographic-results-container" style={{ padding: 0, maxWidth: '100%' }}>
            <div className="demographic-results-header text-center mb-4">
              <h1>Datos Demográficos del Estudiante</h1>
              <p>Información personal y académica recopilada</p>
            </div>

            <div className="demographic-results-content">
              {/* Información Personal */}
              <div className="info-section">
                <h2>
                  <i className="bi bi-person-circle"></i>
                  Información Personal
                </h2>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Número de Boleta:</label>
                    <span>{demographicData.student_id || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Nombre Completo:</label>
                    <span>{demographicData.full_name || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Correo Institucional:</label>
                    <span>{demographicData.institutional_email || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Correo Personal:</label>
                    <span>{demographicData.personal_email || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Sexo:</label>
                    <span>
                      {demographicData.sex === 'Otro' ? demographicData.other_sex : demographicData.sex || 'No especificado'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Rango de Edad:</label>
                    <span>{demographicData.age_range || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Estado:</label>
                    <span>{demographicData.state || 'No especificado'}</span>
                  </div>
                </div>
              </div>

              {/* Información Académica */}
              <div className="info-section">
                <h2>
                  <i className="bi bi-mortarboard"></i>
                  Información Académica
                </h2>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Tipo de Universidad:</label>
                    <span>{demographicData.university_type || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Área de Conocimiento:</label>
                    <span>{demographicData.knowledge_area || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Turno:</label>
                    <span>{demographicData.shift || 'No especificado'}</span>
                  </div>
                  <div className="info-item">
                    <label>Semestre:</label>
                    <span>{demographicData.semester || 'No especificado'}</span>
                  </div>
                </div>
              </div>

              {/* Intereses y Aspiraciones */}
              <div className="info-section">
                <h2>
                  <i className="bi bi-lightbulb"></i>
                  Intereses y Aspiraciones
                </h2>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Disposición para Trabajar:</label>
                    <span>
                      {demographicData.wants_to_work === 'Otra' ? demographicData.other_work : demographicData.wants_to_work || 'No especificado'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Interés en IA y Robótica:</label>
                    <span>
                      {demographicData.wants_ai_robotics === 'Otra' ? demographicData.other_ai_robotics : demographicData.wants_ai_robotics || 'No especificado'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Botones de Acción */}
              <div className="action-buttons text-center mt-4">
                <PDFDownloadLink
                  document={
                    <DemographicPDF
                      userData={userData}
                      demographicData={demographicData}
                    />
                  }
                  fileName={`datos-demograficos-${new Date().toISOString().split('T')[0]}.pdf`}
                  className="btn btn-primary me-2"
                >
                  {({ blob, url, loading, error }) => (
                    <button className="btn btn-primary" disabled={loading}>
                      <i className="bi bi-file-earmark-pdf"></i>
                      {loading ? 'Generando PDF...' : ' Descargar PDF'}
                    </button>
                  )}
                </PDFDownloadLink>

                <button
                  onClick={() => navigate("/demographic-survey")}
                  className="btn btn-outline-primary"
                >
                  <i className="bi bi-pencil"></i> Editar Datos
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DemographicResults;
